<!--
 * @Description: 个体发票-待开票数据（票税修改）
 *               服务商端个体发票Tab + 代办供应商端待开票数据子Tab 共用
 *               scene=1 服务商个体（全功能）；scene=11 代办供应商
 *               （隐藏申请开票按钮 / 工业名称列 / 工业名称搜索 / 操作列）
 * @Date: 2025-09-15 13:36:48
-->
<template>
  <XrkTable
    ref="xrkTableRef"
    :getListAjax="getListAjax"
    :searchConfig="searchConfig"
    :tableConfig="tableConfig"
  />
</template>
<script lang="tsx" setup>
import { ref, computed, getCurrentInstance } from 'vue';
import {
  XrkTable,
  XrkTableProps,
  useTableColumn,
  useDatePickerColumn,
  useInputColumn,
  BaseButton,
  useSelectColumn,
  useCreateModal
} from 'xrk-components';
import { formatter } from '@/utils';
import { individualNotInvoicedPageList } from '@V/invoice/src/data-source/apis';
import InvoiceApplyIndividualDialog from '../invoice-apply-individual-dialog';
import { useRouter, useRoute } from 'vue-router';
// import InvoiceDetailDialog from '@V/invoice/src/components/invoice-detail-dialog';
import { projectNameSearch } from '@V/invoice/src/fields/searchs';
import { SETTLEMENT_STATUS_OPTIONS } from '@V/invoice/src/constants/index';

const props = withDefaults(
  defineProps<{
    /** 场景：1=服务商个体 11=代办供应商 */
    scene?: 1 | 11;
  }>(),
  { scene: 1 }
);

/** 代办供应商场景：隐藏申请开票/工业名称/操作列 */
const isDbgys = computed(() => props.scene === 11);

const router = useRouter();
const route = useRoute();

const xrkTableRef = ref();

const getListAjax = async params => {
  return individualNotInvoicedPageList(params);
};

const searchConfig = ref<XrkTableProps['searchConfig']>({
  searchColumns: [
    useInputColumn({
      prop: 'settlementNo',
      label: '结算单号',
      config: { placeholder: '请输入结算单号', clearable: true }
    }),
    projectNameSearch(),
    // 工业名称（代办供应商场景不展示）
    !isDbgys.value &&
      useInputColumn({
        prop: 'industryName',
        label: '工业名称',
        config: { placeholder: '请输入工业名称', clearable: true }
      }),
    useInputColumn({
      prop: 'serviceProviderName',
      label: '服务商名称',
      config: { placeholder: '请输入服务商名称', clearable: true }
    }),
    useInputColumn({
      prop: 'individualName',
      label: '个体户名称',
      config: { placeholder: '请输入个体户名称', clearable: true }
    }),
    useInputColumn({
      prop: 'operator',
      label: '经营者',
      config: { placeholder: '请输入经营者', clearable: true }
    }),
    useInputColumn({
      prop: 'phone',
      label: '手机号',
      config: { placeholder: '请输入手机号', clearable: true }
    }),
    useDatePickerColumn({
      prop: 'confirmTime',
      label: '结算单确认时间',
      config: {
        type: 'daterange',
        startPlaceholder: '时间始',
        endPlaceholder: '时间止',
        clearable: true
      },
      listener: {
        onChange: (value, searchObj) => {
          if (!value) {
            searchObj.settlementConfirmTimeStart = '';
            searchObj.settlementConfirmTimeEnd = '';
          } else {
            searchObj.settlementConfirmTimeStart = value[0];
            searchObj.settlementConfirmTimeEnd = value[1];
          }
        }
      }
    }),
    useSelectColumn({
      prop: 'settlementStatus',
      label: '结算单状态',
      config: {
        clearable: true,
        placeholder: '请选择',
        options: SETTLEMENT_STATUS_OPTIONS
      }
    })
  ].filter(item => item),
  searchBtn: { show: true, text: '搜索' },
  resetBtn: {
    show: true,
    text: '重置',
    beforeReset: () => {
      if (Object.keys(route.query).length) {
        router.replace({ query: { force: Date.now() } });
      }
    }
  },
  /** 申请开票按钮（代办供应商场景不展示） */
  otherComponents: [
    !isDbgys.value && {
      render() {
        return (
          <BaseButton
            v-resource={[['未开票订单_申请开票']]}
            type="primary2"
            click={async () => {
              useCreateModal(
                InvoiceApplyIndividualDialog,
                {
                  onSuccess() {
                    xrkTableRef.value.initCurrentList();
                  }
                },
                getCurrentInstance()
              );
            }}
          >
            申请开票
          </BaseButton>
        );
      }
    }
  ].filter(item => item)
});

const tableConfig = ref<XrkTableProps['tableConfig']>({
  tableColumns: [
    useTableColumn({
      type: 'index',
      label: '序号',
      width: '60px',
      fixed: 'left'
    }),
    useTableColumn({
      label: '结算单号',
      prop: 'settlementNo',
      width: '180px',
      render: ({ row }) => (
        <span style="color: #e97132; cursor: pointer">{row.settlementNo}</span>
      )
    }),
    useTableColumn({
      label: '项目名称',
      prop: 'projectName',
      minWidth: '140px'
    }),
    // 工业名称（代办供应商场景不展示）
    !isDbgys.value &&
      useTableColumn({
        label: '工业名称',
        prop: 'industryName',
        width: '160px'
      }),
    useTableColumn({
      label: '服务商名称',
      prop: 'serviceProviderName',
      width: '160px'
    }),
    useTableColumn({
      label: '个体户名称',
      prop: 'individualName',
      width: '180px'
    }),
    useTableColumn({ label: '经营者', prop: 'operator', width: '100px' }),
    useTableColumn({
      label: '手机号',
      prop: 'phone',
      width: '130px',
      formatter: ({ cellValue }) => formatter.maskPhone(cellValue)
    }),
    useTableColumn({
      label: '终端结算金额(元)',
      prop: 'terminalSettlementAmount',
      width: '160px',
      formatter: ({ cellValue }) => formatter.fen2yuan(cellValue)
    }),
    // 后端要求去掉
    // useTableColumn({
    //   label: '已申请开票金额(元)',
    //   prop: 'appliedInvoiceAmount',
    //   width: '160px',
    //   formatter: ({ cellValue }) => formatter.fen2yuan(cellValue)
    // }),
    useTableColumn({
      label: '结算单确认时间',
      prop: 'settlementConfirmTime',
      minWidth: '170px',
      formatter: ({ cellValue }) => formatter.date(cellValue)
    }),
    useTableColumn({
      label: '结算单状态',
      prop: 'settlementStatus',
      width: '120px',
      formatter: ({ cellValue }) =>
        formatter.defaultFormat(cellValue, () => {
          const item = SETTLEMENT_STATUS_OPTIONS.find(
            o => o.value === cellValue
          );
          return item?.label;
        })
    }),
    /* 操作列（代办供应商场景不展示） */
    !isDbgys.value &&
      useTableColumn({
        label: '操作',
        prop: 'handlerCol',
        width: '120px',
        fixed: 'right',
        render: ({ row }) => {
          return (
            <>
              {/* <BaseButton
              type="text"
              click={async () => {
                useCreateModal(InvoiceDetailDialog, {
                  id: row.id,
                  detailType: 1,
                  onSuccess() {
                    xrkTableRef.value.initCurrentList();
                  }
                });
              }}
            >
              详情
            </BaseButton> */}
              {/* 票税修改：订单明细 → 新标签页跳转结算明细 */}
              <BaseButton
                type="text"
                click={() => {
                  const url = router.resolve({
                    path: '/main/settlement-mgmt-details',
                    query: {
                      settlementId: row.settlementId,
                      settlementCode: row.settlementNo,
                      projectName: row.projectName,
                      projectStructureType: row.projectStructureType,
                      projectId: row.projectId,
                      projectCode: row.projectCode
                    }
                  }).href;
                  window.open(url, '_blank');
                }}
              >
                订单明细
              </BaseButton>
            </>
          );
        }
      })
  ].filter(item => item)
});
</script>
